import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Geolocation, Position, PermissionStatus, GeolocationPermissionType } from '@capacitor/geolocation';
import { from, Observable, throwError, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AlertController, Platform } from '@ionic/angular';
import { TranslateConfigService } from './translate-config.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class GeolocationService {
  // Ubicación por defecto (Madrid)
  defaultLocation = { lat: 40.4168, lng: -3.7038 };

  constructor(
    private alertController: AlertController,
    private platform: Platform
    ,
    private translateConfigService: TranslateConfigService,
    private translate: TranslateService
  ) { }

  /**
   * Solicita permisos de geolocalización al usuario
   * @returns Observable con el estado de los permisos
   */
  requestPermissions(): Observable<PermissionStatus> {
    if (Capacitor.isNativePlatform()) {
      return from(Geolocation.requestPermissions({
        permissions: ['location']
      })).pipe(
        catchError(error => {
          return throwError(() => new Error(this.translate.instant('GEOLOCATION.PERMISSIONS_ERROR')));
        })
      );
    } else {
      // Web implementation using navigator.permissions
      return from(navigator.permissions.query({ name: 'geolocation' })).pipe(
        map(status => ({ location: status.state } as PermissionStatus)),
        catchError(error => {
          return throwError(() => new Error(this.translate.instant('GEOLOCATION.PERMISSIONS_ERROR')));
        })
      );
    }
  }

  /**
   * Verifica si la aplicación tiene permisos de geolocalización
   * @returns Observable con el estado de los permisos
   */
  checkPermissions(): Observable<PermissionStatus> {
    return from(Geolocation.checkPermissions()).pipe(
      tap(permissionResult => {

      }),
      catchError(error => {
        return throwError(() => new Error(this.translate.instant('GEOLOCATION.PERMISSIONS_CHECK_ERROR')));
      })
    );
  }

  /**
   * Obtiene la posición actual del usuario
   * @param options Opciones de geolocalización (enableHighAccuracy, timeout, maximumAge)
   * @returns Observable con la posición actual (latitud y longitud)
   */
  getCurrentPosition(options?: { enableHighAccuracy?: boolean, timeout?: number, maximumAge?: number }): Observable<Position> {
    // Si estamos en el navegador y no en un dispositivo físico, usar la Web Geolocation API
    if (!Capacitor.isNativePlatform()) {
      return this.getWebPosition(options);
    }

    return from(Geolocation.getCurrentPosition(options)).pipe(
      catchError(error => {
        return throwError(() => new Error(this.translate.instant('GEOLOCATION.POSITION_ERROR')));
      })
    );
  }

  /**
   * Obtiene la posición actual, primero verificando y solicitando permisos si es necesario
   * @returns Observable con la posición actual (latitud y longitud)
   */
  getPositionWithPermissionHandling(): Observable<Position> {
    // Si estamos en el navegador y no en un dispositivo físico, usar la Web Geolocation API
    // navigator.geolocation.getCurrentPosition() triggerea automáticamente el prompt de permisos del navegador
    if (!Capacitor.isNativePlatform()) {
      return this.getWebPosition({ enableHighAccuracy: true }).pipe(
        catchError(() => this.getDefaultPositionAsObservable())
      );
    }

    return this.checkPermissions().pipe(
      switchMap(status => {

        if (status.location === 'granted') {
          return this.getCurrentPosition({ enableHighAccuracy: true });
        } else if (status.location === 'prompt') {
          return this.requestPermissions().pipe(
            switchMap(requestResult => {
              if (requestResult.location === 'granted') {
                return this.getCurrentPosition({ enableHighAccuracy: true });
              } else {
                // Mostrar alerta localizada y luego retornar posición por defecto
                return from(this.showPermissionAlert()).pipe(
                  switchMap(() => this.getDefaultPositionAsObservable())
                );
              }
            })
          );
        } else {
          // Permisos denegados previamente: mostrar alerta localizada y retornar posición por defecto
          return from(this.showPermissionAlert()).pipe(
            switchMap(() => this.getDefaultPositionAsObservable())
          );
        }
      }),
      catchError(error => {
        // Usar ubicación predeterminada en caso de error
        return this.getDefaultPositionAsObservable();
      })
    );
  }

  /**
   * Obtiene la posición usando la Web Geolocation API del navegador.
   * Esto triggerea automáticamente el prompt de permisos del navegador.
   */
  private getWebPosition(options?: { enableHighAccuracy?: boolean, timeout?: number, maximumAge?: number }): Observable<Position> {
    return new Observable<Position>(observer => {
      if (!navigator.geolocation) {
        observer.error(new Error(this.translate.instant('GEOLOCATION.POSITION_ERROR')));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          const pos: Position = {
            coords: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              altitudeAccuracy: position.coords.altitudeAccuracy,
              altitude: position.coords.altitude,
              speed: position.coords.speed,
              heading: position.coords.heading
            },
            timestamp: position.timestamp
          };
          observer.next(pos);
          observer.complete();
        },
        (error: GeolocationPositionError) => {
          observer.error(new Error(this.translate.instant('GEOLOCATION.POSITION_ERROR')));
        },
        {
          enableHighAccuracy: options?.enableHighAccuracy ?? true,
          timeout: options?.timeout ?? 10000,
          maximumAge: options?.maximumAge ?? 0
        }
      );
    });
  }

  /**
   * Muestra una alerta informando al usuario sobre los permisos de ubicación
   */
  private async showPermissionAlert() {
    const header = this.translateConfigService.getItemTranslated('PERMISSIONS.LOCATION_HEADER') || 'Permisos de ubicación';
    const message = this.translateConfigService.getItemTranslated('PERMISSIONS.LOCATION_MESSAGE') || 'Para mostrar tu ubicación en el mapa, necesitamos acceso a tu ubicación. Por favor, concede los permisos desde la configuración de tu dispositivo.';
    const ok = this.translateConfigService.getItemTranslated('COMMON.OK') || 'OK';

    const alert = await this.alertController.create({
      header,
      message,
      buttons: [ok]
    });
    await alert.present();
  }

  /**
   * Devuelve la ubicación predeterminada como un Observable
   */
  private getDefaultPositionAsObservable(): Observable<Position> {


    // Crear un objeto Position con la ubicación predeterminada
    const defaultPosition: Position = {
      coords: {
        latitude: this.defaultLocation.lat,
        longitude: this.defaultLocation.lng,
        accuracy: 10,
        altitudeAccuracy: null,
        altitude: null,
        speed: null,
        heading: null
      },
      timestamp: new Date().getTime()
    };

    return of(defaultPosition);
  }
}
